import { useState, type ReactElement } from 'react'
import { parseAsInteger, useQueryState } from 'nuqs'
import { parseAsSearchQuery } from '@util'
import { type TaskData } from '@terros/common'
import { Group, Stack, Switch, TextInput } from '@mantine/core'
import { useQueryDisclosure } from '@hooks'
import { useTaskManagement, useTranslation } from '@front/model'
import { ChooseDates, Page } from '@components'
import { TaskTable } from './TaskTable'
import { FiltersDrawer } from './FiltersDrawer'
import { useTaskClientState } from './clientState'
import { BulkActions } from './BulkAction'
import { ActiveFilters } from './ActiveFilters'
import { createFileRoute } from '@tanstack/react-router'

type Props = {
  back?: boolean
}

function TaskList({ back }: Props): ReactElement {
  const clientState = useTaskClientState()
  const { teamIds, ownerIds, fromIds, programIds, statuses, criterias, range, setRange } = clientState

  const [archived, { toggle }] = useQueryDisclosure('archived', false)
  const [page, setPage] = useQueryState('page', parseAsInteger.withDefault(1))
  const [query, setQuery] = useQueryState('query', parseAsSearchQuery.withDefault(''))

  const {
    data = [],
    isFetching,
    refetch,
  } = useTaskManagement({
    teamIds,
    ownerIds,
    fromIds,
    programIds,
    statuses,
    criteriaTypes: criterias,
    query,
    archived,
    startDate: range.at(0)?.valueOf(),
    endDate: range.at(1)?.valueOf(),
  })
  const { t } = useTranslation()

  const [selectedTasks, setSelectedTasks] = useState<TaskData[]>([])

  return (
    <Page>
      <Page.Heading back={back} isFetching={isFetching} refetch={refetch} title={t('Tasks')} />
      <Page.FunctionBar>
        <Stack flex={1}>
          <Group className='w-full' justify='space-between'>
            <Group>
              <ChooseDates range={range} setRange={setRange} />
              <FiltersDrawer {...clientState} />
              <TextInput placeholder={t('Filter')} value={query} onChange={(e) => setQuery(e.currentTarget.value)} />
            </Group>
            <Switch
              checked={archived}
              label={t('Show archived tasks')}
              onChange={() => {
                toggle()
                setPage(1)
              }}
            />
          </Group>
          <ActiveFilters {...clientState} />
        </Stack>
      </Page.FunctionBar>
      <Page.Content>
        <TaskTable
          data={data}
          page={page}
          setPage={setPage}
          selectedRecords={selectedTasks}
          setSelectedRecords={setSelectedTasks}
        />
        <BulkActions
          archived={archived}
          selectedTasks={selectedTasks}
          clearSelection={() => setSelectedTasks([])}
          totalTasks={data.length}
          refetch={refetch}
        />
      </Page.Content>
    </Page>
  )
}

export const Route = createFileRoute('/task/management/')({
  component: TaskList,
})
